import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ImageMetadata, fetchPaginatedImages, fetchPopularImages, fetchPublicSearch, fetchServerTime, getAdjacentMonthLinks, MonthLink } from './services/api';
import { formatDateYmd } from './utils/time';
import ImageGrid from './components/ImageGrid';
import ImageRoute from './components/ImageRoute';
import AppliedFilters from './components/AppliedFilters';
import ContentPageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import { getConfig } from './config';
import { useSidebar } from './contexts/SidebarContext';
import { ContentProvider, useContent } from './contexts/ContentContext';
import { TooltipProvider } from './contexts/TooltipContext';
import Tooltip from './components/Tooltip';
import WorksSidebar from './components/WorksSidebar';
import TwitchGuide from './components/TwitchGuide';
import CommandEditor from './components/CommandEditor';
import RelatedLinks from './components/RelatedLinks';
import LandingPage from './components/LandingPage';
import WordGallery from './components/WordGallery';
import AnalyticsLoader from './components/AnalyticsLoader';
import SiteGuide from './components/SiteGuide';
import { guideHelpEvent } from './utils/guideHelp';
import SongGallery from './components/SongGallery';
import SongDetail from './components/SongDetail';
import Header from './components/Header';
import ArticleGallery from './components/ArticleGallery';
import ArticleDetail from './components/ArticleDetail';
import ArticleSidebar from './components/ArticleSidebar';
import { usePageMetadata } from './hooks/usePageMetadata';
import TagSearchModal from './components/TagSearchModal';
import PublicSearchGallery from './components/PublicSearchGallery';
import WorkflowIndex from './components/WorkflowIndex';
import RatingGuide from './components/RatingGuide';
import VisitorFavoritesPage from './components/VisitorFavoritesPage';
import { TagSearchProvider, useTagSearch } from './contexts/TagSearchContext';
import { pageTitle as resolvePageTitle, resolvePage } from './pages';
import { useRestoreListPosition } from './components/BackToListButton';
import IllustrationHome from './components/IllustrationHome';
import IllustrationSubheader from './components/IllustrationSubheader';
import IllustrationScrollActions from './components/IllustrationScrollActions';
import { ImagePreviewProvider } from './contexts/ImagePreviewContext';
import { BrandDesignProvider } from './contexts/BrandDesignContext';

function AppContent() {
  const [images, setImages] = useState<ImageMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const route = resolvePage(window.location.search);
  const { params: urlParams, page, tab, view, word: resolvedWord } = route;
  const isPublicIndexPage = page === 'illustrations' || page === 'search' || page === 'workflow' || page === 'ratings' || page === 'favorites' || page === 'tool';
  const initialPageNumber = parseInt(urlParams.get('pageNumber') || '1', 10);
  const [currentPage, setCurrentPage] = useState(initialPageNumber);

  const { isSidebarOpen, syncRoute } = useSidebar();
  const routeSearch = window.location.search;
  useLayoutEffect(() => { syncRoute(); }, [routeSearch, syncRoute]);
  const { content, language, t } = useContent();
  const [totalItems, setTotalItems] = useState(0);
  const [serverTime, setServerTime] = useState<number | null>(null);
  const [newerMonth, setNewerMonth] = useState<MonthLink | null>(null);
  const [olderMonth, setOlderMonth] = useState<MonthLink | null>(null);
  const [galleryGridWidth, setGalleryGridWidth] = useState<number | null>(null);

  const year = urlParams.get('year');
  const month = urlParams.get('month');
  const popular = urlParams.get('root') === 'popular';
  const hash = urlParams.get('hash');
  const id = urlParams.get('id');
  const color = urlParams.get('color');
  const platform = urlParams.get('platform');
  const [articleDates, setArticleDates] = useState<string[]>([]);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [guideSection, setGuideSection] = useState<string | null>(null);
  const [helpSection, setHelpSection] = useState<string | null>(null);
  const [helpSource, setHelpSource] = useState<'gallery-float' | undefined>();
  useEffect(() => {
    const open = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const id = typeof detail === 'string' ? detail : typeof detail?.id === 'string' ? detail.id : 'about';
      setHelpSection(id);
      setHelpSource(detail?.source === 'gallery-float' && ['search', 'favorites'].includes(id) ? 'gallery-float' : undefined);
    };
    const close = () => { setHelpSection(null); setHelpSource(undefined); };
    window.addEventListener(guideHelpEvent, open);
    window.addEventListener('popstate', close);
    return () => { window.removeEventListener(guideHelpEvent, open); window.removeEventListener('popstate', close); };
  }, []);
  const refreshRoute = useCallback(() => setForceUpdate(prev => !prev), []);
  const touchDetectedRef = useRef(false);
  const { openTagSearch } = useTagSearch();
  const lastTrackedPathRef = useRef<string | null>(null);
  const pendingGalleryScrollTopRef = useRef(false);

  const shouldScrollGalleryUrlToTop = (url: URL) => {
    const params = url.searchParams;
    return params.get('page') === 'gallery' && params.get('view') !== 'image';
  };

  const handleGalleryPageChange = (nextPage: number) => {
    setCurrentPage(nextPage);
    pendingGalleryScrollTopRef.current = true;
    const params = new URLSearchParams(window.location.search);
    if (nextPage <= 1) {
      params.delete('pageNumber');
    } else {
      params.set('pageNumber', String(nextPage));
    }
    const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.pushState({}, '', nextUrl);
  };

  useEffect(() => {
    const handleGuideClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest('a');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      const targetPage = url.searchParams.get('page');
      if (url.origin !== window.location.origin || !['about', 'tool'].includes(targetPage || '')) return;
      event.preventDefault();
      setGuideSection(url.hash.slice(1) || (targetPage === 'tool' ? 'tools' : 'about'));
    };
    const handleLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      const anchor = target?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      if (anchor.getAttribute('target') === '_blank' || anchor.hasAttribute('download')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;

      const scrollBehaviorAttr = anchor.getAttribute('data-scroll-top');
      const scrollBehavior = scrollBehaviorAttr === 'instant' ? 'auto' : 'smooth';

      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.href === window.location.href) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      const from = resolvePage(window.location.search);
      const to = resolvePage(url.search);
      setGuideSection(null);
      const listToDetail = !from.view && ((to.view === 'image' && from.definition.kind === 'images') || ((to.view === 'detail' || to.view === 'song') && from.definition.id === to.definition.id));
      window.history.pushState(listToDetail ? { contentList: { href: window.location.pathname + window.location.search + window.location.hash, scrollY: window.scrollY } } : {}, '', url.href);
      if (shouldScrollGalleryUrlToTop(url)) {
        pendingGalleryScrollTopRef.current = true;
      }
      setForceUpdate(prev => !prev);
      if (scrollBehaviorAttr && window.scrollY > 0) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: scrollBehavior as ScrollBehavior });
        });
      }
    };

    const handlePopState = () => {
      setGuideSection(null);
      setForceUpdate(prev => !prev);
    };

    document.addEventListener('click', handleGuideClick, true);
    document.addEventListener('click', handleLinkClick);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('click', handleGuideClick, true);
      document.removeEventListener('click', handleLinkClick);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
        return;
      }
      const target = event.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;
      if (isTyping) return;
      event.preventDefault();
      openTagSearch();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openTagSearch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nextPageNumber = parseInt(params.get('pageNumber') || '1', 10);
    if (nextPageNumber !== currentPage) {
      setCurrentPage(nextPageNumber);
    }
  }, [forceUpdate, currentPage]);

  useEffect(() => {
    const updateTouchPortraitClass = () => {
      const isPortrait = window.innerHeight > window.innerWidth;
      const shouldEnable = touchDetectedRef.current && isPortrait && (!isSidebarOpen || route.definition.kind === 'images');
      document.body.classList.toggle('touch-portrait', shouldEnable);
    };

    const markTouchDetected = () => {
      touchDetectedRef.current = true;
      updateTouchPortraitClass();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        markTouchDetected();
      }
    };

    window.addEventListener('touchstart', markTouchDetected, { passive: true, once: true });
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    window.addEventListener('resize', updateTouchPortraitClass);
    window.addEventListener('orientationchange', updateTouchPortraitClass);
    updateTouchPortraitClass();

    return () => {
      window.removeEventListener('touchstart', markTouchDetected);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', updateTouchPortraitClass);
      window.removeEventListener('orientationchange', updateTouchPortraitClass);
    };
  }, [isSidebarOpen, route.definition.kind]);

  useEffect(() => {
    let active = true;
    const getImages = async () => {
      try {
        setIsLoading(true); setError(null);
        const { thumbnailsPerPage } = getConfig();
        const time = await fetchServerTime();
        if (!active) return;
        setServerTime(time);

        if (view !== 'image' && popular) {
          const result = await fetchPopularImages(new URLSearchParams({ page: String(currentPage), per_page: String(thumbnailsPerPage) }));
          if (!active) return;
          setImages(result.items); setTotalItems(result.pagination.total_items);
        } else if (view !== 'image' && year && month) {
          const result = await fetchPaginatedImages('month', {
            y: year,
            m: month,
            page: currentPage,
            per_page: thumbnailsPerPage
          });
          if (!active) return;
          setImages(result.items);
          setTotalItems(result.pagination.total_items);
        } else if (view !== 'image') {
          const result = await fetchPublicSearch(new URLSearchParams({ recent: '1', page: String(currentPage), per_page: String(thumbnailsPerPage) }));
          if (!active) return;
          setImages(result.items); setTotalItems(result.pagination.total_items);
        }
      } catch (err) {
        if (!active) return;
        setError(t('common.imageFetchError'));
        console.error(err);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    if (page === 'gallery' && tab === 'gallery') {
      if ((year || month) && (!year || !month)) {
        setIsLoading(false);
        setImages([]); setTotalItems(0);
        return;
      }
      getImages();
    } else {
      setIsLoading(false);
    }
    return () => { active = false; };
  }, [page, view, year, month, tab, currentPage, t, forceUpdate, popular]);

  useEffect(() => {
    if (
      page !== 'gallery' ||
      tab !== 'gallery' ||
      view === 'image' ||
      !year ||
      !month ||
      isLoading ||
      !pendingGalleryScrollTopRef.current
    ) {
      return;
    }

    pendingGalleryScrollTopRef.current = false;
    if (window.location.hash) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }, [page, tab, view, year, month, currentPage, isLoading, images.length]);

  useEffect(() => {
    if (page !== 'gallery' || tab !== 'gallery' || view === 'image' || !year || !month) {
      setNewerMonth(null);
      setOlderMonth(null);
      return;
    }

    let isActive = true;
    getAdjacentMonthLinks(year, month)
      .then(adjacent => {
        if (!isActive) return;
        setNewerMonth(adjacent.newer);
        setOlderMonth(adjacent.older);
      })
      .catch(error => {
        console.error('Failed to find adjacent months:', error);
        if (!isActive) return;
        setNewerMonth(null);
        setOlderMonth(null);
      });

    return () => {
      isActive = false;
    };
  }, [page, tab, view, year, month]);

  const getPageMetadata = () => {
    let label = resolvePageTitle(route.definition, content, language, t);
    if (page === 'word' || page === 'color') label = t('common.wordTitle', { label, word: page === 'word' ? resolvedWord || '' : color || '' });
    const title = t('common.pageTitle', { title: label, name: content.siteName });
    const description = route.definition.id === 'home' ? t('metadata.homeDescription')
      : page === 'about' ? content.ui[language].aboutPage.disclaimer[0]
      : route.definition.kind === 'images' ? content.gallery.introduction[language].points.join(' ')
      : label;
    return { title, description };
  };

  const { title: pageTitle, description: pageDescription } = getPageMetadata();

  usePageMetadata({
    title: pageTitle,
    description: pageDescription,
    enabled: view !== 'detail' && view !== 'song' && view !== 'image'
  });

  useEffect(() => {
    const basePath = page ? `/${page}` : '/';
    let nextPath = view ? `${basePath}/${view}` : basePath;

    if (page === 'word' && resolvedWord) {
      nextPath = `${basePath}/${encodeURIComponent(resolvedWord)}`;
    } else if (page === 'color' && color) {
      nextPath = `${basePath}/${encodeURIComponent(color)}`;
    } else if (view === 'image' && year && month && hash && id) {
      nextPath = `${basePath}/${view}/${year}/${month}/${id}_${hash}`;
    } else if (page === 'article' && view === 'detail' && year && month && id) {
      const publishedAtRaw = urlParams.get('published_at');
      const publishedAtJst = publishedAtRaw ? formatDateYmd(publishedAtRaw) : null;
      if (publishedAtJst) {
        nextPath = `${basePath}/${view}/${year}/${month}/${publishedAtJst}_${id}`;
      } else {
        nextPath = `${basePath}/${view}/${year}/${month}/${id}`;
      }
    } else if (view === 'song' && id && platform) {
      nextPath = `${basePath}/${view}/${platform}_${id}`;
    }
    if (lastTrackedPathRef.current === nextPath) {
      return;
    }

    lastTrackedPathRef.current = nextPath;
    const gtag = (window as typeof window & { gtag?: (...args: any[]) => void }).gtag;
    if (typeof gtag !== 'function') {
      return;
    }

    gtag('event', 'page_view', {
      page_path: nextPath,
      page_location: window.location.href,
      page_title: document.title
    });
  }, [page, view, year, month, hash, id, platform, resolvedWord, color]);

  useRestoreListPosition(page === 'gallery' && !view && !isLoading);

  const directGuide = page === 'about' || page === 'tool';
  const guide = <>{(directGuide || guideSection !== null) && <SiteGuide
    initialSection={guideSection || window.location.hash.slice(1) || (page === 'tool' ? 'tools' : 'about')}
    direct={directGuide} onClose={() => {
      setGuideSection(null);
      if (directGuide) { window.history.replaceState({}, '', '/'); refreshRoute(); }
    }} />}{helpSection !== null && <SiteGuide compact initialSection={helpSection} canHide={helpSource === 'gallery-float'} onClose={() => { setHelpSection(null); setHelpSource(undefined); }} />}</>;
  if (directGuide || (page !== 'gallery' && page !== 'word' && page !== 'color' && page !== 'article' && !isPublicIndexPage)) {
    return <><Header /><IllustrationSubheader /><LandingPage /><TagSearchModal />{guide}</>;
  }

  const { thumbnailsPerPage } = getConfig();
  const paginatedImages = images; // Images are already paginated by server
  const totalPages = Math.max(1, Math.ceil(totalItems / thumbnailsPerPage));
  const normalizedCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const firstVisibleImage = totalItems > 0 ? (normalizedCurrentPage - 1) * thumbnailsPerPage + 1 : 0;
  const lastVisibleImage = totalItems > 0 ? Math.min(normalizedCurrentPage * thumbnailsPerPage, totalItems) : 0;
  const rangeStatusLabel = t('common.rangeStatus', { first: firstVisibleImage, last: lastVisibleImage, total: totalItems });
  const imageListHeader = <ContentPageHeader
    count={!isLoading && !error ? rangeStatusLabel : undefined}>
    <AppliedFilters />
    {!isLoading && !error && <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={thumbnailsPerPage}
      onPageChange={handleGalleryPageChange} newerMonth={newerMonth} olderMonth={olderMonth} className="lab-list-pagination" />}
  </ContentPageHeader>;

  const footer = (
      <footer className="bg-white dark:bg-gray-800 ivory:bg-coffee-100 shadow-md mt-8">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 text-center text-gray-500 dark:text-gray-400 ivory:text-coffee-600">
          <p>{t('common.copyright', { year: new Date().getFullYear(), name: content.siteName })}</p>
          <a href="/?page=tool" className="inline-block mt-2 mr-4 text-sm underline">{t('common.tool')}</a>
          <a
            href="/?page=about"
            className="inline-flex items-center justify-center mt-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 ivory:text-coffee-600 ivory:hover:text-coffee-800 underline underline-offset-4"
          >
            {content.ui[language].aboutPage.title}
          </a>
        </div>
      </footer>
  );

  const renderMainContent = () => {
    if (page === 'illustrations') return <IllustrationHome key={urlParams.get('root') || 'home'} />;
    if (page === 'favorites') return <VisitorFavoritesPage footer={footer} />;
    if (view === 'image') {
      return <ImageRoute search={window.location.search} onResolve={refreshRoute} />;
    }

    if (view === 'song' && id) {
      return <SongDetail id={id} />;
    }

    if (page === 'word' && resolvedWord) {
      return <WordGallery word={resolvedWord} />;
    }

    if (page === 'color' && color) {
      return <WordGallery word={color} mode="color" />;
    }

    if (page === 'search') return <PublicSearchGallery />;
    if (page === 'workflow') return <WorkflowIndex />;
    if (page === 'ratings') return <RatingGuide />;

    if (page === 'article') {
      if (view === 'detail' && year && month && id) {
        return <ArticleDetail year={year} month={month} id={id} />;
      }
      return <ArticleGallery onDatesChange={setArticleDates} />;
    }

    if (isLoading && tab === 'gallery') {
      return <>{imageListHeader}<p role="status" className="dark:text-gray-300">{content.ui[language].common.loading}</p></>;
    }
    if (error && tab === 'gallery') {
      return <>{imageListHeader}<p role="alert" className="text-red-500">{error}</p><button type="button" className="lab-button" onClick={refreshRoute}>{t('common.retry')}</button></>;
    }

    switch (tab) {
      case 'twitch_guide':
        return <TwitchGuide />;
      case 'command_editor':
        return <CommandEditor standalone />;
      case 'related_links':
        return <RelatedLinks />;
      case 'works':
        return <SongGallery platform={platform} />;
      case 'gallery':
      default:
        return (
          <>
            {imageListHeader}
            {!images.length && <p role="status">{t('searchPublicSearchGallery.text11')}</p>}
            <ImageGrid images={paginatedImages} year={year} month={month} onGridWidthChange={setGalleryGridWidth} showMonthNavigation={false} />
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={thumbnailsPerPage}
              onPageChange={handleGalleryPageChange}
              newerMonth={newerMonth}
              olderMonth={olderMonth}
              gridWidth={galleryGridWidth}
            />
          </>
        );
    }
  };

  return (
    <ImagePreviewProvider routeKey={window.location.pathname + window.location.search + window.location.hash}>
    <div className={`lab-shell min-h-screen flex flex-col ${page === 'favorites' ? 'lab-favorites-shell' : ''}`}>
      <AnalyticsLoader />
      <Header />
      <IllustrationSubheader />
      <div className="lab-main-row flex flex-grow">
        {page === 'article' && (
          <ArticleSidebar
            dates={view === 'detail' ? [] : articleDates}
            showDates={view !== 'detail'}
          />
        )}
        {tab === 'works' && <WorksSidebar selectedPlatform={platform} />}
        <main className={`min-w-0 flex-grow p-4 transition-all duration-300 ease-in-out ${isSidebarOpen && (tab === 'works' || page === 'article') ? 'md:ml-64' : 'ml-0'}`}>
          {['images', 'works', 'article'].includes(route.definition.kind) && view !== 'image' && <IllustrationScrollActions key={routeSearch} favorites={page === 'favorites'} showGuide={route.definition.kind === 'images'} />}
          {renderMainContent()}
        </main>
      </div>
      {page !== 'favorites' && footer}
      <TagSearchModal />
      {guide}
    </div>
    </ImagePreviewProvider>
  );
};

const App: React.FC = () => {
  return (
    <ContentProvider>
      <BrandDesignProvider>
      <TagSearchProvider>
        <TooltipProvider>
          <AppContent />
          <Tooltip />
        </TooltipProvider>
      </TagSearchProvider>
      </BrandDesignProvider>
    </ContentProvider>
  );
};

export default App;
